import Dexie, { Table } from 'dexie'
import store from '@/store'

export interface VideoMessage {
    id: number
    from: 'send' | 'receive'
    type: 'image' | 'video' | 'text'
    image?: string
    videoUrl?: string
    coverUrl?: string
    content?: string
    status?: 'thinking' | 'processing' | 'success' | 'error'
    errorMessage?: string
    taskId?: string
    seoTitle?: string
    like?: number
    createTime: number
    userId: number | string
}

export interface VideoHistory {
    id?: number
    createTime: number
    userId: number | string
    messageList: VideoMessage[]
}

export class VideoDatabase extends Dexie {
    videoHistory!: Table<VideoHistory, number>

    constructor() {
        super('VideoDatabase')

        this.version(1).stores({
            videoHistory: '++id, userId, createTime'
        })

        // @ts-ignore
        this.videoHistory.hook('creating', (primKey: any, obj: any) => {
            obj.createTime = obj.createTime || Date.now()
            obj.userId = store.getters['user/userId']
        })
    }
}

export const videoDB = new VideoDatabase()

export const videoDbUtils = {
    async createHistory(): Promise<{ hId: number }> {
        const history: Omit<VideoHistory, 'id'> = {
            createTime: Date.now(),
            userId: store.getters['user/userId'],
            messageList: []
        }
        const hId = await videoDB.videoHistory.add(history as any)
        return { hId }
    },

    async addMessage(historyId: number, message: Omit<VideoMessage, 'id'>): Promise<number> {
        const history = await videoDB.videoHistory.get(historyId)
        if (!history) throw new Error('History not found')

        const messageId = history.messageList.length
        const fullMessage: VideoMessage = { ...message, id: messageId }

        await videoDB.videoHistory.update(historyId, {
            messageList: [...history.messageList, fullMessage]
        })

        return messageId
    },

    async updateMessage(historyId: number, messageId: number, updates: Partial<VideoMessage>): Promise<void> {
        const history = await videoDB.videoHistory.get(historyId)
        if (!history) throw new Error('History not found')

        const idx = history.messageList.findIndex(m => m.id === messageId)
        if (idx === -1) throw new Error('Message not found')

        const updatedList = [...history.messageList]
        updatedList[idx] = { ...updatedList[idx], ...updates }

        await videoDB.videoHistory.update(historyId, {
            messageList: updatedList
        })
    },

    async getHistory(historyId: number): Promise<VideoHistory | undefined> {
        return videoDB.videoHistory.get(historyId)
    },

    async getLatestHistory(): Promise<VideoHistory | undefined> {
        const userId = store.getters['user/userId']
        const results = await videoDB.videoHistory
            .where('userId')
            .equals(userId)
            .toArray()
        if (results.length === 0) return undefined
        results.sort((a, b) => b.createTime - a.createTime)
        return results[0]
    },

    async getUserHistories(): Promise<VideoHistory[]> {
        const userId = store.getters['user/userId']
        const results = await videoDB.videoHistory
            .where('userId')
            .equals(userId)
            .toArray()
        return results.sort((a, b) => a.createTime - b.createTime)
    },

    async deleteHistory(historyId: number): Promise<void> {
        await videoDB.videoHistory.delete(historyId)
    }
}
